import { HealthcareGuidanceCard } from '../healthcare-guidance-card/healthcare-guidance-card';
import { PhysicianFinderDisclaimer } from '../physician-finder-disclaimer/physician-finder-disclaimer';
import { ValuableResourceCard } from '../valuable-resource-card/valuable-resource-card';
import DFU_Brochure from '../../../../../../public/images/dfu_brochure.webp';
import VLU_Brochure from '../../../../../../public/images/vlu_brochure.webp';
import Puraply_Brochure from '../../../../../../public/images/Puraply_brochure.webp';
import Derm_Brochure from '../../../../../../public/images/Derm_brochure.webp';
import Affinity_Brochure from '../../../../../../public/images/Affinity_brochure.webp';
import Cygnus_Dual_Brochure from '../../../../../../public/images/Cygnus_Dual_brochure.webp';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ScrollArea,
  SelectItem,
  SelectContent,
  Select,
  SelectTrigger,
  SelectValue,
} from '@shared/shared-shadcn-ui';
import { DoctorDtoResponse } from 'apps/woundscantwait-com/src/packages/doctors/doctors';
import { FC } from 'react';
import { nearDoctorsResponseToTableDataHelper } from '../find-near-doctor/helpers/helpers';
import { cn } from '@shared/shared-utils';
import { Loader2 } from 'lucide-react';
import { AppLink } from 'apps/woundscantwait-com/src/libs/components/components';
import PhoneIcon from '../../../../../../public/svg/phone.svg';
import Link from 'next/link';

type Properties = {
  doctors: DoctorDtoResponse[];
  zip: string;
  radius: string;
  onSearchWithinChange: (value: string) => void;
  onBackToSearch: () => void;
  isLoading: boolean;
};

const ClosestLocations: FC<Properties> = ({
  doctors,
  zip,
  onSearchWithinChange,
  onBackToSearch,
  radius,
  isLoading,
}: Properties) => {
  return (
    <div className="w-full bg-white border-b-[3px] pb-16 border-b-ogn-bright-yellow rounded-[6px]">
      <div className="relative bg-white rounded-[14px] w-full max-w-[1225px] text-ogn-blue h-[625px]">
        <div className="text-[18px] lg:text-[20px] pt-6 flex lg:justify-between justify-center items-center text-[#707070]">
          <div className="lg:ml-8 font-normal">
            Locations closest to <span className="text-ogn-blue">{zip}</span>
          </div>
          <div
            onClick={onBackToSearch}
            className="lg:block hidden text-[18px] font-bold text-ogn-blue mr-6 cursor-pointer"
          >
            Don’t see your doctor?
          </div>
        </div>
        {isLoading && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full flex items-center justify-center z-10">
            <Loader2 className="w-16 h-16 animate-spin" />
          </div>
        )}
        <ScrollArea className="h-[500px] mt-6 relative">
          <Table className="w-full h-full relative table-fixed">
            <TableHeader>
              <TableRow className="!border-b-0 text-[14px] lg:text-[18px] whitespace-nowrap sticky top-0 bg-white font-[550] lg:font-bold font-benton-sans text-[#707070] uppercase">
                {[
                  'STATE',
                  'ZIP',
                  'HEALTHCARE PROVIDER',
                  'HCP',
                  'SPECIALTY',
                  'INSTITUTION',
                  'PHONE',
                ].map((item, index) => (
                  <TableHead
                    className={cn(
                      'pb-6 h-auto shrink-0 overflow-hidden text-ellipsis whitespace-nowrap',
                      {
                        'hidden lg:table-cell': ![
                          'ZIP',
                          'HCP',
                          'PHONE',
                        ].includes(item),
                        'lg:hidden': item === 'HCP',
                        'w-[8%] pl-6': item === 'STATE',
                        'w-[75px] lg:w-[8%]': item === 'ZIP',
                        'w-[18%]': item === 'HEALTHCARE PROVIDER',
                        'w-3/4 lg:w-[15%]': item === 'HCP',
                        'w-1/4 lg:w-[10%]': item === 'SPECIALTY',
                        'w-1/4 lg:w-[15%]': item === 'INSTITUTION',
                        'w-[100px] lg:w-[12%]': item === 'PHONE',
                      },
                    )}
                    key={index}
                  >
                    <div
                      className={cn('w-full overflow-hidden text-ellipsis', {
                        'min-w-[250px]': item === 'HEALTHCARE PROVIDER',
                      })}
                    >
                      {item}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="relative">
              {nearDoctorsResponseToTableDataHelper(doctors).map(
                (item, index) => (
                  <TableRow
                    className={cn(
                      'bg-white text-[14px] lg:text-[18px] font-bold text-[#707070] font-benton-sans',
                      {
                        'bg-[#F8F8F8] hover:bg-[#F8F8F8]': index % 2 === 0,
                      },
                    )}
                    key={index}
                  >
                    <TableCell className="hidden lg:table-cell truncate max-w-0 pl-6">
                      {[...item.state].slice(0, 2).join('')}
                    </TableCell>
                    <TableCell className="truncate max-w-0 pr-0">
                      {item.zip}
                    </TableCell>
                    <TableCell className="truncate max-w-0" title={item.name}>
                      <div className="min-w-[250px]">{item.name}</div>
                    </TableCell>
                    <TableCell
                      className="hidden lg:table-cell truncate max-w-0"
                      title={item.specialty}
                    >
                      {item.specialty}
                    </TableCell>
                    <TableCell
                      className="hidden lg:table-cell text-ogn-blue truncate max-w-0"
                      title={item.institution}
                    >
                      <Link
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${item.institution}, ${item.city}, ${item.state} ${item.zip}`)}`}
                        target="_blank"
                      >
                        {item.institution}
                      </Link>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell truncate max-w-0">
                      {item.phone}
                    </TableCell>
                    <TableCell className="lg:hidden">
                      <AppLink
                        className="text-ogn-blue inline-flex items-center gap-x-2"
                        to={`tel:${item.phone}`}
                      >
                        Call
                        <PhoneIcon className="w-4 h-4" />
                      </AppLink>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
          {doctors.length <= 4 && !isLoading && (
            <div className="text-[22px] mt-12 py-2 lg:text-[24px] flex flex-col items-center justify-center w-full">
              <div className="font-roboto-condensed text-center font-bold text-ogn-blue mt-6 lg:mt-0">
                Not seeing anything nearby? Adjust your search.
              </div>
              <div className="mt-8 flex lg:flex-row flex-col items-center gap-3">
                <span className="font-roboto-condensed font-bold text-ogn-blue">
                  Search within
                </span>
                <Select value={radius} onValueChange={onSearchWithinChange}>
                  <SelectTrigger className="!relative !z-[30] inline-flex items-center justify-center rounded-full bg-white border-0 w-auto h-auto leading-normal py-3 text-[20px] lg:text-[22px] drop-shadow-[0_3px_6px_rgba(0,0,0,0.161)] px-6 [&>svg]:h-6 [&>svg]:w-6">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent
                    withPortal={false}
                    side="bottom"
                    className="bg-[#F2F2F2] border-0 rounded-none -top-12 !z-[25]"
                  >
                    {[5, 10, 15, 20, 25, 30, 35, 45, 50].map((item, index) => (
                      <SelectItem
                        key={item}
                        value={item.toString()}
                        className={cn(
                          'hover:bg-[#E5E5E5] focus:bg-[#E5E5E5] text-[18px] lg:text-[20px] cursor-pointer py-2',
                          'data-[state=checked]:bg-ogn-blue data-[state=checked]:text-white',
                          {
                            'mt-14': index === 0,
                          },
                        )}
                      >
                        {item} miles
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </ScrollArea>
        <div className="absolute bottom-0 left-0 w-full h-[16px] z-[10] pointer-events-none bg-transparent">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1226"
            height="16"
            viewBox="0 0 1226 16"
            fill="none"
          >
            <path
              d="M0 16L1226 16V0L0 0V16Z"
              fill="url(#paint0_linear_5_4637)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5_4637"
                x1="613"
                y1="16"
                x2="613"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-opacity="0.078" />
                <stop offset="1" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
      <div
        onClick={onBackToSearch}
        className="lg:hidden text-center text-[18px] font-bold text-ogn-blue cursor-pointer mt-6"
      >
        Don’t see your doctor?
      </div>
      <div className="lg:pl-12 mt-6 bg-ogn-white">
        <PhysicianFinderDisclaimer className="max-w-[960px]" />
        <div className="mt-12">
          <div className="text-[20px] lg:text-[22px] text-ogn-blue font-bold">
            Download these valuable resources:
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6 max-w-[780px] mt-6">
            <ValuableResourceCard
              imageBrochure={DFU_Brochure}
              label={
                <>
                  Diabetic Foot Ulcer <br /> Patient Brochure{' '}
                </>
              }
              link="pdf/apligraf-dfu-patient-brochure.pdf"
            />
            <ValuableResourceCard
              imageBrochure={VLU_Brochure}
              label={
                <>
                  Venous Leg Ulcer <br /> Patient Brochure{' '}
                </>
              }
              link="pdf/apligraf-vlu-patient-brochure.pdf"
            />
            <ValuableResourceCard
              imageBrochure={Puraply_Brochure}
              label={
                <>
                  PuraPly AM & XT <br /> Patient Brochure{' '}
                </>
              }
              link="pdf/PuraPly_AM_XT_Patient_Education_Brochure.pdf"
            />
            <ValuableResourceCard
              imageBrochure={Derm_Brochure}
              label={
                <>
                  Dermatology <br /> Patient Brochure{' '}
                </>
              }
              link="pdf/Dermatology_Patient_Brochure_OI-AWCP1138.pdf"
            />
            <ValuableResourceCard
              imageBrochure={Affinity_Brochure}
              label={
                <>
                  Affinity & Novachor <br /> Patient Brochure{' '}
                </>
              }
              link="pdf/Affinity_Patient_Brochure.pdf"
            />
            <ValuableResourceCard
              imageBrochure={Cygnus_Dual_Brochure}
              label={
                <>
                  Cygnus Dual <br /> Patient Brochure{' '}
                </>
              }
              link="pdf/CYGNUS_Dual_Patient_Brochure_OI-CYG-0005.pdf"
            />
          </div>
        </div>
        <HealthcareGuidanceCard />
      </div>
    </div>
  );
};

export { ClosestLocations };
